import React from 'react';
import Collection from 'react-virtualized/dist/commonjs/Collection';
import { getTelegramWebApp, isWebMobile } from '@24i/nxg-sdk-higgs/src/utils';
import {
    TIME_24H_30_MIN_INTERVALS,
    TIME_12H_30_MIN_INTERVALS,
} from '@24i/nxg-core-utils/src/constants';
import IconButton from '@24i/nxg-sdk-gluons/src/components/buttons/IconButton';
import { View, Text } from '@24i/nxg-sdk-quarks';
import { sidebarConfig } from '../../../../utils/constants';
import { formattedTime } from '../../../../utils/DateHelpers';
import getStyles from './styles';
import { TimelineProps, CellRendererProps, CellGetterProps } from './types';

const Timeline = (props: TimelineProps) => {
    const {
        currentTime = new Date(),
        scrollLeft = 0,
        showCurrentTime = false,
        timeLabelPosition = 0,
        timelineRef,
        theme,
        width,
        currentTimeLabelText,
        onScrollLeftPress = () => {},
        onScrollRightPress = () => {},
        is12HourClock,
        timeFormat,
        showLeftArrow,
        showRightArrow,
    } = props;
    const currentLabelLeft =
        getTelegramWebApp() && isWebMobile()
            ? sidebarConfig.sidebarWidthMobile
            : Math.max(theme.sidebar.container.width, theme.sidebar.container.minWidth);
    const styles = getStyles(theme);
    const hours =
        timeFormat?.is12HourClock ?? is12HourClock
            ? TIME_12H_30_MIN_INTERVALS
            : TIME_24H_30_MIN_INTERVALS;

    const timeLineOffset = theme.showTimelineArrows ? 40 : 0;

    const cellRenderer = (cellProps: CellRendererProps) => {
        const { index, key, style } = cellProps;

        return index === hours.length ? (
            <div key={key}>
                <View
                    style={{
                        ...styles.currentTimeLabelContainer,
                        left:
                            timeLabelPosition -
                            theme.currentTime.label.width / 2 +
                            theme.currentTime.line.width / 2 +
                            currentLabelLeft,
                    }}
                >
                    <Text style={{ ...styles.currentTimeLabelText }}>
                        {currentTimeLabelText ||
                            formattedTime(timeFormat || is12HourClock, currentTime)}
                    </Text>
                </View>
            </div>
        ) : (
            <View
                pointerEvents="none"
                key={key}
                style={{
                    ...style,
                    ...{
                        left: index * style.width + currentLabelLeft,
                    },
                    ...styles.columnContainer,
                }}
            >
                <Text
                    style={{
                        ...styles.text,
                        ...theme.timeline.text,
                    }}
                >
                    {hours[index]}
                </Text>
                <Text
                    style={{
                        ...styles.text,
                        ...theme.timeline.hoursLine,
                    }}
                >
                    |
                </Text>
            </View>
        );
    };

    const cellSizeAndPositionGetter = (cellProps: CellGetterProps) => {
        const { index } = cellProps;

        return index === hours.length
            ? {
                  height: 20,
                  width: 30,
                  x: timeLabelPosition,
                  y: 0,
              }
            : {
                  height: theme.timeline.container.height,
                  width: 30 * theme.PIXELS_PER_MIN,
                  x: index * 30 * theme.PIXELS_PER_MIN + currentLabelLeft,
                  y: 0,
              };
    };

    return (
        <View>
            <Collection
                style={styles.collection}
                cellCount={showCurrentTime ? hours.length + 1 : hours.length}
                cellRenderer={cellRenderer}
                cellSizeAndPositionGetter={cellSizeAndPositionGetter}
                height={theme.timeline.container.height}
                width={width - timeLineOffset}
                timeLabelPosition={timeLabelPosition}
                scrollLeft={scrollLeft}
                verticalOverscanSize={0}
                horizontalOverscanSize={0}
                ref={timelineRef}
            />
            <View
                style={{
                    ...theme.divider,
                    width: width + currentLabelLeft,
                }}
            ></View>
            {theme.showTimelineArrows && (
                <View style={{ ...styles.arrowsWrapper, width: width + 10 }}>
                    {showLeftArrow && (
                        <IconButton
                            iconName="onPrimary-back"
                            additionalIconStyles={styles.arrows}
                            additionalContainerStyles={styles.leftArrowContainer}
                            onPress={onScrollLeftPress}
                            iconSize={20}
                        />
                    )}
                    {showRightArrow && (
                        <IconButton
                            iconName="onPrimary-back_rtl"
                            onPress={onScrollRightPress}
                            additionalIconStyles={styles.arrows}
                            additionalContainerStyles={styles.rightArrowContainer}
                            iconSize={20}
                        />
                    )}
                </View>
            )}
        </View>
    );
};

export default Timeline;
